import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ArrowLeft, CheckCircle, XCircle, Sparkles } from "lucide-react";
import SettingsToggle from "@/components/SettingsToggle";
import { consumeRememberedAuthReturn } from "@/lib/authReturn";

export default function VerifyEmail() {
  const tokenFromUrl = new URLSearchParams(window.location.search).get("token") || "";
  const [step, setStep] = useState<"pending" | "success" | "error" | "missing">(
    tokenFromUrl ? "pending" : "missing",
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [returnTo] = useState(() =>
    tokenFromUrl ? consumeRememberedAuthReturn() : null,
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Auto-verify if a token is present in the URL
  const verifyMutation = useMutation({
    mutationFn: async (t: string) => {
      const res = await apiRequest("POST", "/api/auth/verify-email", { token: t });
      return res.json();
    },
    onSuccess: async () => {
      setStep("success");
      // Refresh the signed-in account immediately so profile/banners update.
      await queryClient.refetchQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: (error: any) => {
      setStep("error");
      setErrorMsg(error.message || "The link may be invalid or expired.");
    },
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/resend-verification", {});
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Email sent!", description: "Check your inbox and use the verification link within 5 minutes." });
      setResendCooldown(60);
    },
    onError: (error: any) => {
      toast({ title: "Couldn't send", description: error.message || "Please try again later.", variant: "destructive" });
    },
  });

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Auto-trigger verification when token is in URL
  useEffect(() => {
    if (tokenFromUrl && step === "pending") {
      verifyMutation.mutate(tokenFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl]);

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center py-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <Link href="/">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" aria-label="Back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <SettingsToggle />
        </div>

        <div className="text-center mb-8">
          <div className="animate-float mb-6">
            <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <h1 className="font-semibold text-2xl mb-2 text-foreground">ZEASTRO</h1>
          <p className="text-muted-foreground text-sm">Email Verification</p>
        </div>

        <Card className="cosmic-card border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
          <CardHeader className="pb-4">
            <CardTitle className="text-center text-lg font-medium text-foreground">
              {step === "success" ? "Email Verified!" : step === "error" ? "Verification Failed" : "Verify Your Email"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Success state */}
            {step === "success" && (
              <div className="text-center py-4 space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-foreground font-medium">Your email has been verified successfully!</p>
                <p className="text-sm text-muted-foreground">You now have full access to your ZEASTRO AI account.</p>
                <Link href={returnTo || "/"}>
                  <Button className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white">
                    {returnTo ? "Continue →" : "Go to Home →"}
                  </Button>
                </Link>
              </div>
            )}

            {/* Missing and invalid links have no manual-token fallback. */}
            {(step === "error" || step === "missing") && (
              <div className="text-center py-4 space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
                <p className="text-foreground font-medium">
                  {step === "missing" ? "Open the link from your email" : "Could not verify email"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {step === "missing"
                    ? "Use the Verify Email link we sent you within 5 minutes. If it is missing or expired, sign in and request a new email."
                    : errorMsg}
                </p>
                <Link href="/login">
                  <Button variant="outline" className="w-full">
                    Sign in
                  </Button>
                </Link>
                <Button
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending || resendCooldown > 0}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : resendMutation.isPending
                    ? "Sending..."
                    : "Resend verification email"}
                </Button>
              </div>
            )}

            {/* A token-bearing link is consumed automatically. */}
            {step === "pending" && (
              <div className="text-center py-8">
                <i className="fas fa-spinner fa-spin text-2xl text-primary mb-3" aria-hidden="true"></i>
                <p className="text-muted-foreground text-sm">Verifying your email…</p>
              </div>
            )}

            <div className="text-center mt-6">
              <Link href="/">
                <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer inline-flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" />
                  Back to Home
                </span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
